/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Layout,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import UserContext from '../../layout/UserContext';
import checkPermission from '../../../components/Authorization/Util';
import {
  getSyncLogsService,
  getSyncLogSummaryService,
  syncBeaconsService,
} from '../../../services/appCmsServices/BeaconService';
import { showSyncResult, syncErrorReason } from './syncMessages';

const { Content } = Layout;
const { Option } = Select;

const STATUS_COLORS = {
  SUCCESS: '#52c41a',
  PARTIAL: '#fa8c16',
  FAILED: '#f5222d',
};
const DATE_FORMAT = 'DD MMM YYYY, HH:mm';

const StatusTag = ({ status }) => (
  <Tag style={{ color: STATUS_COLORS[status], borderColor: STATUS_COLORS[status] }}>
    {status}
  </Tag>
);

const formatDate = value => (value ? moment(value).format(DATE_FORMAT) : '');

const describeTrigger = entry =>
  entry.triggerType === 'MANUAL'
    ? 'Manual' + (entry.triggeredBy ? ' · ' + entry.triggeredBy : '')
    : 'Scheduled';

const durationOf = entry =>
  entry.finishedAt
    ? Math.max(0, moment(entry.finishedAt).diff(moment(entry.startedAt), 'seconds')) + ' s'
    : '';

const countOrDash = (value, entry) => (entry.status === 'FAILED' ? '–' : value);

const detailsOf = entry => (entry && entry.details) || { skipped: [], conflicts: [] };

const SyncLogs = () => {
  const dispatch = useDispatch();
  const currentUser = useContext(UserContext);
  let canSync = false;
  try {
    canSync = !!checkPermission(currentUser, 'appcms', 'write');
  } catch (e) {
    canSync = false;
  }

  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState({ content: [], totalElements: 0 });
  const [filters, setFilters] = useState({ page: 0, size: 10, status: undefined });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [summaryResponse, logsResponse] = await Promise.all([
        dispatch(getSyncLogSummaryService()),
        dispatch(getSyncLogsService(filters)),
      ]);
      setSummary(summaryResponse.data);
      setLogs(logsResponse.data);
    } catch (error) {
      message.error('Could not load the sync logs: ' + syncErrorReason(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await dispatch(syncBeaconsService());
      showSyncResult(response.data);
    } catch (error) {
      message.error('Sync failed: ' + syncErrorReason(error), 10);
    } finally {
      setSyncing(false);
      load();
    }
  };

  const lastRun = summary && summary.lastRun;
  const lastSuccess = summary && summary.lastSuccessfulRun;
  const nextRun = summary && summary.nextScheduledRun;

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => <StatusTag status={status} />,
    },
    { title: 'Started', dataIndex: 'startedAt', key: 'startedAt', render: formatDate },
    { title: 'Duration', key: 'duration', render: (text, entry) => durationOf(entry) },
    { title: 'Trigger', key: 'trigger', render: (text, entry) => describeTrigger(entry) },
    { title: 'Added', dataIndex: 'added', key: 'added', align: 'right', render: countOrDash },
    { title: 'Updated', dataIndex: 'updated', key: 'updated', align: 'right', render: countOrDash },
    { title: 'Skipped', dataIndex: 'skipped', key: 'skipped', align: 'right', render: countOrDash },
    {
      title: 'Conflicts',
      dataIndex: 'conflicts',
      key: 'conflicts',
      align: 'right',
      render: countOrDash,
    },
    {
      title: 'Details',
      key: 'details',
      render: (text, entry) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => setSelected(entry)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Content style={{ margin: '16px 16px 0' }}>
      {lastRun && lastRun.status === 'FAILED' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="The last sync failed"
          description={
            formatDate(lastRun.startedAt) +
            '. ' +
            (lastRun.errorMessage || '') +
            (lastSuccess
              ? ' Beacon data is from the sync of ' + formatDate(lastSuccess.startedAt) + '.'
              : '') +
            (nextRun ? ' The next attempt is at ' + moment(nextRun).format('HH:mm') + '.' : '')
          }
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Last successful sync"
              value={lastSuccess ? moment(lastSuccess.startedAt).format('HH:mm') : 'None yet'}
            />
            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              {lastSuccess
                ? moment(lastSuccess.startedAt).format('DD MMM YYYY') +
                  (lastSuccess.status === 'PARTIAL'
                    ? ' · partial, ' + lastSuccess.skipped + ' skipped'
                    : '')
                : 'No sync has completed yet'}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Next scheduled sync"
              value={nextRun ? moment(nextRun).format('HH:mm') : 'Off'}
            />
            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              {nextRun ? moment(nextRun).fromNow() + ' · every 3 hours' : 'Scheduled sync is turned off'}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Failed runs, last 7 days"
              value={summary ? summary.failedLast7Days : 0}
              valueStyle={{
                color: summary && summary.failedLast7Days > 0 ? STATUS_COLORS.FAILED : undefined,
              }}
            />
            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              {'of ' + (summary ? summary.runsLast7Days : 0) + ' runs'}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Open major/minor conflicts"
              value={summary ? summary.openConflicts : 0}
              valueStyle={{
                color: summary && summary.openConflicts > 0 ? STATUS_COLORS.PARTIAL : undefined,
              }}
            />
            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              Shown in red on Show Beacons, cannot be assigned
            </div>
          </Card>
        </Col>
      </Row>

      <div style={{ padding: 16, background: '#fff' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 500 }}>Sync history</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <Select
              value={filters.status || ''}
              style={{ width: 160 }}
              onChange={value =>
                setFilters(prev => ({ ...prev, page: 0, status: value || undefined }))
              }
            >
              <Option value="">All statuses</Option>
              <Option value="SUCCESS">Success</Option>
              <Option value="PARTIAL">Partial</Option>
              <Option value="FAILED">Failed</Option>
            </Select>
            <Popconfirm
              title="Sync all beacons from Kontakt.io now?"
              placement="bottomRight"
              disabled={!canSync}
              onConfirm={handleSync}
            >
              <Button type="primary" icon={<SyncOutlined />} loading={syncing} disabled={!canSync}>
                Sync now
              </Button>
            </Popconfirm>
          </span>
        </div>

        <Table
          columns={columns}
          dataSource={logs.content}
          loading={loading}
          rowKey={entry => entry.id}
          locale={{ emptyText: 'No sync has run yet' }}
          onChange={pagination =>
            setFilters(prev => ({
              ...prev,
              page: pagination.current - 1,
              size: pagination.pageSize,
            }))
          }
          pagination={{
            current: filters.page + 1,
            pageSize: filters.size,
            total: logs.totalElements,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>

      <Modal
        title={selected ? 'Sync run · ' + formatDate(selected.startedAt) : ''}
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={[
          <Button key="close" onClick={() => setSelected(null)}>
            Close
          </Button>,
        ]}
        width={720}
      >
        {selected && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">
                <StatusTag status={selected.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Trigger">{describeTrigger(selected)}</Descriptions.Item>
              <Descriptions.Item label="Started">{formatDate(selected.startedAt)}</Descriptions.Item>
              <Descriptions.Item label="Duration">{durationOf(selected)}</Descriptions.Item>
              <Descriptions.Item label="Added">{countOrDash(selected.added, selected)}</Descriptions.Item>
              <Descriptions.Item label="Updated">
                {countOrDash(selected.updated, selected)}
              </Descriptions.Item>
              {selected.errorMessage && (
                <Descriptions.Item label="Error" span={2}>
                  <span style={{ color: STATUS_COLORS.FAILED }}>{selected.errorMessage}</span>
                </Descriptions.Item>
              )}
            </Descriptions>
            <List
              size="small"
              bordered
              header={<strong>Skipped beacons</strong>}
              locale={{ emptyText: 'None' }}
              dataSource={detailsOf(selected).skipped}
              renderItem={item => <List.Item>{item}</List.Item>}
              style={{ marginBottom: 16 }}
            />
            <List
              size="small"
              bordered
              header={<strong>Major/minor conflicts</strong>}
              locale={{ emptyText: 'None' }}
              dataSource={detailsOf(selected).conflicts}
              renderItem={item => <List.Item>{item}</List.Item>}
            />
          </>
        )}
      </Modal>
    </Content>
  );
};

export default SyncLogs;
